import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { title?: string };

const baseProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
};

export function AwardIcon({ title, ...props }: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      {title ? <title>{title}</title> : null}
      <circle cx="12" cy="9" r="6" />
      <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
    </svg>
  );
}

export function MicroscopeIcon({ title, ...props }: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      {title ? <title>{title}</title> : null}
      <path d="M6 18h8" />
      <path d="M3 22h18" />
      <path d="M14 22a7 7 0 1 0 0-14h-1" />
      <path d="M9 14h2" />
      <path d="M9 12a2 2 0 0 1-2-2V6h6v4a2 2 0 0 1-2 2Z" />
      <path d="M12 6V3a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v3" />
    </svg>
  );
}

export function AtomIcon({ title, ...props }: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      {title ? <title>{title}</title> : null}
      <circle cx="12" cy="12" r="1" />
      <path d="M20.2 20.2c2.04-2.03.02-7.36-4.5-11.9-4.54-4.52-9.87-6.54-11.9-4.5-2.04 2.03-.02 7.36 4.5 11.9 4.54 4.52 9.87 6.54 11.9 4.5Z" />
      <path d="M15.7 15.7c4.52-4.54 6.54-9.87 4.5-11.9-2.03-2.04-7.36-.02-11.9 4.5-4.52 4.54-6.54 9.87-4.5 11.9 2.03 2.04 7.36.02 11.9-4.5Z" />
    </svg>
  );
}

export function WavesIcon({ title, ...props }: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      {title ? <title>{title}</title> : null}
      <path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
      <path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
      <path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
    </svg>
  );
}

export function ChevronRightIcon({ title, ...props }: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      {title ? <title>{title}</title> : null}
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

export function ChevronUpIcon({ title, ...props }: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      {title ? <title>{title}</title> : null}
      <path d="m18 15-6-6-6 6" />
    </svg>
  );
}

export function ChevronDownIcon({ title, ...props }: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      {title ? <title>{title}</title> : null}
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function MenuIcon({ title, ...props }: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      {title ? <title>{title}</title> : null}
      <path d="M3 12h18" />
      <path d="M3 6h18" />
      <path d="M3 18h18" />
    </svg>
  );
}

export function InfoIcon({ title, ...props }: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      {title ? <title>{title}</title> : null}
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  );
}

export function DownloadIcon({ title, ...props }: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      {title ? <title>{title}</title> : null}
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

export function CheckIcon({ title, ...props }: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      {title ? <title>{title}</title> : null}
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function XIcon({ title, ...props }: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      {title ? <title>{title}</title> : null}
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export function TranslateIcon({ title, ...props }: IconProps) {
  return (
    <svg {...baseProps} viewBox="0 0 16 16" strokeWidth={1.5} {...props}>
      {title ? <title>{title}</title> : null}
      <path d="M8.60935 11.3333H13.392M8.60935 11.3333L7.33398 14M8.60935 11.3333L10.5195 7.33935C10.6734 7.01751 10.7504 6.8566 10.8557 6.80575C10.9473 6.76152 11.054 6.76152 11.1456 6.80575C11.2509 6.8566 11.3279 7.01751 11.4818 7.33935L13.392 11.3333M13.392 11.3333L14.6673 14M1.33398 3.33333H5.33398M5.33398 3.33333H7.66732M5.33398 3.33333V2M7.66732 3.33333H9.33398M7.66732 3.33333C7.33656 5.30486 6.56904 7.09079 5.44435 8.58962M6.66732 9.33333C6.25897 9.14987 5.84242 8.89472 5.44435 8.58962M5.44435 8.58962C4.54266 7.89851 3.73583 6.95109 3.33398 6M5.44435 8.58962C4.37456 10.0153 2.98161 11.1812 1.33398 12" />
    </svg>
  );
}
